import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";
import { AccountCircleOutlined } from "@mui/icons-material";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "components/shared/Tooltip";
import { useLogout } from "context/LogoutContext";
import { useUser } from "context/UserContext";
import useGetUnreadNotificationCount from "hooks/api/REST/notifications/useGetUnreadNotificationCount";
import useIsFeatureFlagOn from "hooks/useIsFeatureFlagOn";
import {
  DEFAULT_NOTIFICATIONS_POLLING_INTERVAL,
  NOTIFICATIONS_CURRENT_USER_ID,
  QueryAPIKey,
} from "utils/constants/api.constants";
import { FeatureFlagName } from "utils/constants/featureFlags.constants";
import { Path } from "utils/constants/paths.constants";
import { ChangeLanguageMenu } from "./ChangeLanguageMenu";
import MainNavigationList from "./MainNavigationList";
import Notifications from "./Notifications";
import {
  AlignLeftIcon,
  AlignRightIcon,
  BellIcon,
  BellWithDotIcon,
} from "assets/icons";
import LogoLarge from "assets/images/AccumulusTech_Logo_White.svg";
import LogoSmall from "assets/images/AccumulusTech_Logo_White-Mark.svg";
import styles from "./GlobalNavigation.styles";

type TGlobalNavigationProps = {
  isVisible: boolean;
};

const GlobalNavigation = ({ isVisible }: TGlobalNavigationProps) => {
  const { t } = useTranslation("common");

  const location = useLocation();

  const [isOpen, setIsOpen] = useState(true);

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const { user } = useUser();

  const queryClient = useQueryClient();

  const { logout } = useLogout();

  const { isSuccess, unreadNotificationCount } = useGetUnreadNotificationCount(
    NOTIFICATIONS_CURRENT_USER_ID,
    { read: false, size: 1 },
    DEFAULT_NOTIFICATIONS_POLLING_INTERVAL,
  );

  const hasUnreadNotifications = useMemo(
    () =>
      isSuccess &&
      unreadNotificationCount !== undefined &&
      unreadNotificationCount > 0,
    [isSuccess, unreadNotificationCount],
  );

  const shouldShowTranslateMenu = useIsFeatureFlagOn(
    FeatureFlagName.ShowTranslateMenu,
  );

  useEffect(() => {
    // Refetch the full notification data if there are new unread notifications
    if (unreadNotificationCount && unreadNotificationCount > 0) {
      queryClient.invalidateQueries({
        queryKey: [
          QueryAPIKey.GetNotificationsForUser,
          NOTIFICATIONS_CURRENT_USER_ID,
        ],
      });
    }
  }, [unreadNotificationCount, queryClient]);

  // Close notification when navigation occurs
  useEffect(() => {
    setIsNotificationsOpen(false);
  }, [location]);

  const handleNotificationsMenuClick = () => {
    if (!isNotificationsOpen && isOpen) {
      setIsOpen(false);
    }

    setIsNotificationsOpen((prevOpenedState) => !prevOpenedState);
  };

  const handleLogout = () => {
    logout({
      shouldRestorePath: false,
      shouldLogoutEverywhere: true,
    }).catch(console.error);
  };

  const handleDrawerOpen = () => {
    setIsOpen(true);
  };

  const handleDrawerClose = () => {
    setIsOpen(false);
  };

  const handleNotificationsDrawerClose = () => {
    setIsNotificationsOpen(false);
  };

  const notificationMenuItemStyle = useMemo(() => {
    if (isOpen) {
      if (isNotificationsOpen) {
        return styles.notificationMenuItemActive;
      } else {
        return styles.notificationMenuItem;
      }
    } else {
      if (isNotificationsOpen) {
        return styles.notificationMenuItemDrawerClosedActive;
      } else {
        return styles.notificationMenuItemDrawerClosed;
      }
    }
  }, [isOpen, isNotificationsOpen]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Drawer
        data-testid="left-hand-drawer"
        data-qaid="left-hand-drawer"
        variant="permanent"
        anchor="left"
        open={isOpen}
        sx={isOpen ? styles.drawerOpen : styles.drawerClosed}
      >
        <Stack direction="column" sx={styles.container}>
          <Box
            sx={isOpen ? styles.logoContainerOpen : styles.logoContainerClosed}
            role="banner"
          >
            <img
              data-testid="left-hand-drawer-logo"
              data-qaid="left-hand-drawer-logo"
              src={isOpen ? LogoLarge : LogoSmall}
              alt={t("accumulusTechnologies")}
            />
          </Box>
          <Box sx={styles.mainNavContainer}>
            <MainNavigationList isSidebarExpanded={isOpen} />
          </Box>
          <Box
            sx={styles.additionalNavContainer}
            role={t("navigation.notifications.title")}
          >
            <List disablePadding sx={styles.navMenuList}>
              <ListItem disablePadding sx={notificationMenuItemStyle}>
                <ListItemButton
                  data-qaid="left-hand-navigation-notifications"
                  aria-label={t(
                    "navigation.listItemButtonNotificationsAriaLabel",
                  )}
                  sx={styles.navItem}
                  onClick={handleNotificationsMenuClick}
                >
                  <ListItemIcon>
                    {hasUnreadNotifications ? (
                      <BellWithDotIcon
                        data-qaid="left-hand-navigation-unread-notifications-icon"
                        aria-label={t(
                          "navigation.notificationIconWithDotTitle",
                        )}
                      />
                    ) : (
                      <BellIcon
                        data-qaid="left-hand-navigation-notifications-icon"
                        aria-label={t("navigation.notificationIcon")}
                      />
                    )}
                  </ListItemIcon>
                  {isOpen && (
                    <ListItemText
                      primary={t("navigation.notificationButton")}
                      sx={styles.navMenuItemButtonText}
                    />
                  )}
                </ListItemButton>
              </ListItem>

              {shouldShowTranslateMenu && (
                <ChangeLanguageMenu isSidebarExpanded={isOpen} />
              )}

              <ListItem
                disablePadding
                sx={
                  isOpen ? styles.profileMenuItem : styles.profileMenuItemClosed
                }
              >
                <ListItemButton
                  data-qaid="left-hand-navigation-profile"
                  aria-label={t("navigation.listItemButtonProfileAriaLabel")}
                  sx={styles.navItem}
                  component={NavLink}
                  to={Path.Profile}
                >
                  <ListItemIcon>
                    <AccountCircleOutlined
                      sx={styles.profileMenuIcon}
                      data-qaid="left-hand-navigation-profile-icon"
                    />
                  </ListItemIcon>
                  {isOpen && (
                    <ListItemText
                      primary={user?.firstName}
                      sx={styles.navMenuItemButtonText}
                    />
                  )}
                </ListItemButton>
                {isOpen && (
                  <Tooltip
                    placement="right"
                    title={t("navigation.logoutTooltipTitle")}
                  >
                    <IconButton
                      data-qaid="left-hand-navigation-logout-button"
                      sx={styles.profileMenuItemLogoutArrowIcon}
                      onClick={handleLogout}
                      aria-label={t("navigation.logoutIconButtonAriaLabel")}
                      disableFocusRipple
                    >
                      <LogoutIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
            </List>
            <Divider
              sx={isOpen ? styles.divider : styles.dividerDrawerClosed}
            />
            <List disablePadding sx={styles.navMenuList}>
              <ListItem
                disablePadding
                sx={
                  isOpen
                    ? styles.drawerToggleMenuItem
                    : styles.drawerToggleMenuItemDrawerClosed
                }
              >
                <ListItemButton
                  data-qaid="left-hand-navigation-close-drawer-button"
                  sx={styles.navItem}
                  aria-label={t(
                    "navigation.listItemButtonDrawerToggleAriaLabel",
                  )}
                  onClick={isOpen ? handleDrawerClose : handleDrawerOpen}
                >
                  <ListItemIcon>
                    {isOpen ? (
                      <AlignLeftIcon
                        data-qaid="left-hand-navigation-close-nav-icon"
                        aria-label={t("navigation.closeNavIconTitle")}
                      />
                    ) : (
                      <AlignRightIcon
                        data-qaid="left-hand-navigation-open-nav-icon"
                        aria-label={t("navigation.openNavIconTitle")}
                      />
                    )}
                  </ListItemIcon>
                  {isOpen && (
                    <ListItemText
                      primary={t("navigation.closeMenuIconTitle")}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Stack>
      </Drawer>
      <Notifications
        isOpen={isNotificationsOpen}
        onClose={handleNotificationsDrawerClose}
      />
    </>
  );
};

export default GlobalNavigation;
